import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ConfirmButton } from "./ConfirmButton";

const TIERS = ["customer", "in_discussion", "partner"];

export const PassesView = ({ data, api, authHeaders, onSaved }) => {
  const [form, setForm] = useState({ name: "", company: "", project: "", tier: "customer", note: "" });
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  const [broadcast, setBroadcast] = useState("");
  const [history, setHistory] = useState([]);
  const [pushing, setPushing] = useState(false);

  useEffect(() => {
    fetch(`${api}/admin/broadcasts`, { headers: authHeaders })
      .then((r) => r.json())
      .then((d) => setHistory(d.broadcasts || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const push = async () => {
    if (!broadcast.trim()) return;
    setPushing(true);
    try {
      const res = await fetch(`${api}/admin/broadcast`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify({ message: broadcast.trim() }),
      });
      const doc = await res.json();
      if (!res.ok) throw new Error();
      toast.success(`Sent to ${doc.recipients} active code holders.`);
      setHistory((h) => [doc, ...h]);
      setBroadcast("");
    } catch {
      toast.error("The update didn't send. Please try again.");
    }
    setPushing(false);
  };

  if (!data) return <p className="text-base font-medium text-[#D6D6D6]">Loading...</p>;

  const field = "rounded-xl bg-[#050505] border border-[#2E2E2E] px-4 py-3 text-[15px] font-medium text-white placeholder:text-[#D6D6D6] outline-none focus:border-[#FF5C5C] w-full";

  const create = async (e) => {
    e.preventDefault();
    if (!form.name) return;
    setBusy(true);
    try {
      const res = await fetch(`${api}/admin/passes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify(form),
      });
      const doc = await res.json();
      if (!res.ok) throw new Error();
      setCreated(doc);
      setForm({ name: "", company: "", project: "", tier: "customer", note: "" });
      toast.success(`Code created: ${doc.code}`);
      onSaved();
    } catch {
      toast.error("Couldn't create the code. Please try again.");
    }
    setBusy(false);
  };

  const revoke = async (id) => {
    await fetch(`${api}/admin/passes/${id}`, { method: "DELETE", headers: authHeaders });
    toast.success("Code revoked.");
    onSaved();
  };

  return (
    <div className="space-y-6" data-testid="passes-view">
      <div className="rounded-2xl border border-[#2E2E2E] bg-[#111111] p-6 max-w-2xl space-y-3">
        <p className="text-base font-bold text-[#FF5C5C] mb-2">Send an update to clients</p>
        <p className="text-[15px] font-medium text-[#D6D6D6] leading-relaxed">Send a status note to everyone with an active access code. It shows up in their client panel, the agent knows about it, and anyone with an email on file gets a copy.</p>
        <textarea className={field} rows={3} placeholder="What do your clients need to know right now?" value={broadcast} onChange={(e) => setBroadcast(e.target.value)} data-testid="broadcast-input" />
        <button onClick={push} disabled={pushing || !broadcast.trim()} data-testid="broadcast-push-button"
          className="rounded-xl bg-[#DC2626] text-white font-semibold text-base px-6 py-3 hover:bg-[#b91c1c] disabled:opacity-40 transition-colors duration-150">
          {pushing ? "Sending..." : `Send to all clients (${data.filter((p) => p.active).length})`}
        </button>
        {history.length > 0 && (
          <div className="border-t border-[#2E2E2E] pt-3 space-y-3 max-h-44 overflow-y-auto chat-scroll">
            {history.map((b) => (
              <div key={b.id} className="text-[15px] font-medium" data-testid="broadcast-row">
                <span className="text-[#D6D6D6]">{new Date(b.created_at).toLocaleString()} · {b.recipients} recipients</span>
                <p className="text-[#D6D6D6]">{b.message}</p>
                <p className="text-[14px] mt-0.5" data-testid="broadcast-receipts">
                  <span className={(b.seen_by || []).length > 0 ? "text-[#4ADE80]" : "text-[#D6D6D6]"}>
                    Seen by {(b.seen_by || []).length} of {b.recipients}
                  </span>
                  {(b.seen_by || []).length > 0 && (
                    <span className="text-[#D6D6D6]"> — {(b.seen_by || []).map((s) => s.name).join(", ")}</span>
                  )}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <form onSubmit={create} className="rounded-2xl border border-[#2E2E2E] bg-[#111111] p-6 max-w-2xl space-y-3">
        <p className="text-base font-bold text-white mb-2">Create an access code</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input className={field} placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pass-name-input" />
          <input className={field} placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="pass-company-input" />
          <input className={field} placeholder="Project" value={form.project} onChange={(e) => setForm({ ...form, project: e.target.value })} data-testid="pass-project-input" />
          <select className={field} value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} data-testid="pass-tier-select">
            {TIERS.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
          </select>
        </div>
        <textarea className={field} rows={2} placeholder="A note they'll see when they sign in (status, next steps...)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="pass-note-input" />
        <button type="submit" disabled={busy || !form.name} data-testid="pass-create-button"
          className="rounded-xl bg-white text-black font-semibold text-base px-6 py-3 hover:bg-[#D6D6D6] disabled:opacity-40 transition-colors duration-150">
          {busy ? "Creating..." : "Create code"}
        </button>
        {created && (
          <p className="text-base font-semibold text-[#4ADE80]" data-testid="pass-created-code">
            Share this code with {created.name}: <span className="tracking-[0.15em] border border-[#4ADE80] rounded-lg px-3 py-1">{created.code}</span>
          </p>
        )}
      </form>

      <div className="rounded-2xl border border-[#2E2E2E] bg-[#050505]">
        <p className="text-base font-bold text-white p-5 pb-3">Active codes ({data.filter((p) => p.active).length})</p>
        <div className="overflow-x-auto pb-2">
          <table className="w-full text-[17px] font-medium">
            <thead>
              <tr className="text-[#D6D6D6] font-semibold text-left">
                <th className="p-4">Code</th><th className="p-4">Name</th><th className="p-4">Project</th>
                <th className="p-4">Tier</th><th className="p-4">Uses</th><th className="p-4">Status</th><th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((p) => (
                <tr key={p.id} className="odd:bg-[#111111] text-[#D6D6D6]" data-testid="pass-row">
                  <td className="p-4 tracking-[0.1em] text-white">{p.code}</td>
                  <td className="p-4">{p.name}</td>
                  <td className="p-4">{p.project || p.company || "—"}</td>
                  <td className="p-4">{(p.tier || "").replace("_", " ")}</td>
                  <td className="p-4">{p.uses || 0}</td>
                  <td className={`p-4 font-semibold ${p.active ? "text-[#4ADE80]" : "text-[#FF5C5C]"}`}>{p.active ? "Active" : "Revoked"}</td>
                  <td className="p-4">
                    {p.active && (
                      <ConfirmButton onConfirm={() => revoke(p.id)} testId="pass-revoke-button" confirmLabel="Sure? Revoke it"
                        className="flex items-center gap-1.5 text-[15px] font-semibold text-[#D6D6D6] hover:text-[#FF5C5C] transition-colors duration-150"
                        confirmClassName="flex items-center gap-1.5 text-[15px] font-semibold text-[#FF5C5C] whitespace-nowrap">
                        Revoke
                      </ConfirmButton>
                    )}
                  </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={7} className="p-4 text-[#D6D6D6]">No codes created yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
